import { DownloadOutlined } from '@ant-design/icons';
import ModalEcho from '../Modal/ModalEcho';
import useShowPerformanceData from './usePerformanceData';
import ShowPerformanceTableContainerEcho from './ShowPerformanceTableContainerEcho';
import { useFilterContext, useDerivedNetworkContext } from '@/context';
import { downloadFile } from '@/utils/dataDownloadUtils';
import { useFileFetcher } from '@/services/queryUtils';
import type { DateRange, DownloadConfig, ModalProps } from '@/types';
import { mixpanelTrack } from '@/utils/mixpanelUtils/asyncMixpanel';
import { MIXPANEL_EVENT, RevenueDashboardWidget } from '@/utils/mixpanelUtils/events';

/**
 * This is the main modal component for the show performance modal.
 * It is used to display the show performance data in a modal.
 * It is also used to download the data as a CSV file.
 *
 * @param renderTrigger - The trigger component for the modal.
 * @returns The modal component.
 */
const ShowPerformanceModalEcho = ({ renderTrigger }: ModalProps) => {
  const fetchFile = useFileFetcher();
  const { selectedNetwork } = useDerivedNetworkContext();
  const { filters } = useFilterContext();

  const { refetch } = useShowPerformanceData(selectedNetwork, {
    from: filters.dateRange.from,
    to: filters.dateRange.to,
  });

  const handleCSVExport = async (dateRange: DateRange | undefined) => {
    if (!dateRange) return;

    try {
      const path = `/v2/networks/${selectedNetwork?.id}/ongoing/export/total?from=${dateRange.from}&to=${dateRange.to}&files=shows`;
      const { blob, fileName } = await fetchFile(path);
      downloadFile(blob, fileName);

      mixpanelTrack(MIXPANEL_EVENT.earnings.showPerformance.downloadClicked, {
        widget: RevenueDashboardWidget.ShowPerformance,
        page: 'earnings',
        networkName: selectedNetwork?.name || '',
        start: dateRange.from,
        end: dateRange.to,
      });
    } catch (error) {
      console.error('Error downloading show performance data', error);
    }
  };

  const handleClose = () => {
    mixpanelTrack(MIXPANEL_EVENT.earnings.showPerformance.closed, {
      widget: RevenueDashboardWidget.ShowPerformance,
      page: 'earnings',
    });
  };

  return (
    <ModalEcho
      hashProp="show-performance"
      title="Show performance"
      renderTrigger={renderTrigger}
      onPrefetch={refetch}
      onClose={handleClose}
      showDateRangeFilter
      downloadConfig={{
        onDownload: (dateRange) => handleCSVExport(dateRange),
        downloadButtonText: 'Download .csv',
        downloadButtonIcon: DownloadOutlined as DownloadConfig['downloadButtonIcon'],
      }}
      onDateRangeOpen={() => {
        mixpanelTrack(MIXPANEL_EVENT.earnings.showPerformance.dateOpened, {
          widget: RevenueDashboardWidget.ShowPerformance,
          page: 'earnings',
        });
      }}
      onDateRangeApply={(dateRange) => {
        mixpanelTrack(MIXPANEL_EVENT.earnings.showPerformance.dateApplied, {
          widget: RevenueDashboardWidget.ShowPerformance,
          page: 'earnings',
          start: dateRange.from,
          end: dateRange.to,
        });
      }}
      onDateRangePresetSelect={(preset) => {
        mixpanelTrack(MIXPANEL_EVENT.earnings.showPerformance.datePresetSelected, {
          widget: RevenueDashboardWidget.ShowPerformance,
          page: 'earnings',
          preset,
        });
      }}
    >
      {(dateRange) => (
        <ShowPerformanceTableContainerEcho
          selectedNetwork={selectedNetwork}
          dateRange={dateRange}
        />
      )}
    </ModalEcho>
  );
};

export default ShowPerformanceModalEcho;
